<template>
  <div class="permission-config-container">
    <div class="page-header">
      <div class="header-left">
        <i class="el-icon-arrow-left" @click="goBack"></i>
        <span class="page-title">权限配置</span>
      </div>
    </div>

    <div class="config-content">
      <el-row :gutter="20">
        <!-- 左侧：角色列表 -->
        <el-col :span="8">
          <el-card class="role-list-card">
            <div slot="header">
              <span>选择角色</span>
              <el-button type="warning" size="mini" style="float: right; margin-top: -4px;" @click="initDefaultPermissions" :loading="initLoading">
                初始化默认权限
              </el-button>
            </div>
            <div class="role-list">
              <div
                v-for="role in roles"
                :key="role.roleCode"
                class="role-item"
                :class="{ active: selectedRole === role.roleCode }"
                @click="selectRole(role.roleCode)"
              >
                <div class="role-icon" :style="{ background: role.color }">
                  <i :class="role.icon"></i>
                </div>
                <div class="role-info">
                  <div class="role-name">{{ role.roleName }}</div>
                  <div class="role-perm-count">{{ role.permissionCount }} 个权限</div>
                </div>
                <i class="el-icon-arrow-right" v-if="selectedRole === role.roleCode"></i>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧：权限配置 -->
        <el-col :span="16">
          <el-card class="permission-card" v-if="selectedRole">
            <div slot="header">
              <span>功能权限配置 - {{ currentRoleName }}</span>
              <el-button type="primary" size="small" style="float: right;" @click="savePermission">
                保存配置
              </el-button>
            </div>

            <!-- PC端工作台权限 -->
            <div class="perm-group">
              <div class="perm-group-title">
                <i class="el-icon-monitor"></i> 电脑端工作台权限
                <span class="perm-hint">（控制PC端工作台显示哪些功能模块）</span>
              </div>
              <el-checkbox-group v-model="workbenchPermissions">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-checkbox label="employee-manage">
                      <span class="perm-label">员工管理</span>
                      <span class="perm-desc">员工列表入口</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="role-manage">
                      <span class="perm-label">角色管理</span>
                      <span class="perm-desc">角色列表入口</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="permission-config">
                      <span class="perm-label">权限配置</span>
                      <span class="perm-desc">权限配置入口</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="checkin-manage">
                      <span class="perm-label">考勤管理</span>
                      <span class="perm-desc">考勤统计入口</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="leave-approve">
                      <span class="perm-label">请假审批</span>
                      <span class="perm-desc">审批请假申请</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="makeup-approve">
                      <span class="perm-label">补卡审批</span>
                      <span class="perm-desc">审批补卡申请</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="student-manage">
                      <span class="perm-label">学员管理</span>
                      <span class="perm-desc">学员列表、培训统计</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="customer-manage">
                      <span class="perm-label">客户管理</span>
                      <span class="perm-desc">客户列表、跟进记录</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="payment-audit">
                      <span class="perm-label">缴费审核</span>
                      <span class="perm-desc">审核缴费凭证</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="driver-manage">
                      <span class="perm-label">司机任务</span>
                      <span class="perm-desc">任务列表、行程记录</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="coach-manage">
                      <span class="perm-label">教练功能</span>
                      <span class="perm-desc">培训签到、考试登记</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="finance-manage">
                      <span class="perm-label">财务管理</span>
                      <span class="perm-desc">收款、退款台账</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="salary-manage">
                      <span class="perm-label">薪资核算</span>
                      <span class="perm-desc">薪资计算、工资台账</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="sales-report">
                      <span class="perm-label">业绩报表</span>
                      <span class="perm-desc">业务员业绩统计</span>
                    </el-checkbox>
                  </el-col>
                </el-row>
              </el-checkbox-group>
            </div>

            <!-- 手机端工作台权限 -->
            <div class="perm-group">
              <div class="perm-group-title">
                <i class="el-icon-mobile-phone"></i> 手机端工作台权限
                <span class="perm-hint">（控制手机端工作台显示哪些功能模块）</span>
              </div>
              <el-checkbox-group v-model="appPermissions">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-checkbox label="app-checkin">
                      <span class="perm-label">考勤打卡</span>
                      <span class="perm-desc">打卡、请假、补卡</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-student">
                      <span class="perm-label">学员管理</span>
                      <span class="perm-desc">学员列表、我的学员</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-customer">
                      <span class="perm-label">客户资料</span>
                      <span class="perm-desc">客户跟进</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-driver">
                      <span class="perm-label">司机任务</span>
                      <span class="perm-desc">任务列表、行程记录</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-coach">
                      <span class="perm-label">教练功能</span>
                      <span class="perm-desc">培训签到、考试登记</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-finance">
                      <span class="perm-label">财务管理</span>
                      <span class="perm-desc">财务报表</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-salary">
                      <span class="perm-label">薪资核算</span>
                      <span class="perm-desc">查看工资</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="app-sales">
                      <span class="perm-label">业绩统计</span>
                      <span class="perm-desc">业绩报表</span>
                    </el-checkbox>
                  </el-col>
                </el-row>
              </el-checkbox-group>
            </div>

            <!-- 数据权限范围 -->
            <div class="perm-group">
              <div class="perm-group-title">
                <i class="el-icon-data-analysis"></i> 数据权限范围
              </div>
              <el-radio-group v-model="dataScope">
                <el-radio label="ALL">
                  <span class="perm-label">全部数据</span>
                  <span class="perm-desc">可查看所有员工/学员数据</span>
                </el-radio>
                <el-radio label="ROLE">
                  <span class="perm-label">本角色数据</span>
                  <span class="perm-desc">只能查看同角色员工的数据</span>
                </el-radio>
                <el-radio label="DEPT">
                  <span class="perm-label">本部门数据</span>
                  <span class="perm-desc">只能查看本部门数据</span>
                </el-radio>
                <el-radio label="SELF">
                  <span class="perm-label">仅本人数据</span>
                  <span class="perm-desc">只能查看自己的数据</span>
                </el-radio>
              </el-radio-group>
            </div>

            <!-- 操作权限 -->
            <div class="perm-group">
              <div class="perm-group-title">
                <i class="el-icon-operation"></i> 操作权限
              </div>
              <el-checkbox-group v-model="operationPermissions">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-checkbox label="export">
                      <span class="perm-label">数据导出</span>
                      <span class="perm-desc">导出Excel报表</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="import">
                      <span class="perm-label">数据导入</span>
                      <span class="perm-desc">批量导入数据</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="delete">
                      <span class="perm-label">删除权限</span>
                      <span class="perm-desc">允许删除数据</span>
                    </el-checkbox>
                  </el-col>
                  <el-col :span="8">
                    <el-checkbox label="approve-final">
                      <span class="perm-label">终审权限</span>
                      <span class="perm-desc">最终审批权限</span>
                    </el-checkbox>
                  </el-col>
                </el-row>
              </el-checkbox-group>
            </div>

            <!-- 考勤打卡规则 -->
            <div class="perm-group">
              <div class="perm-group-title">
                <i class="el-icon-alarm-clock"></i> 考勤打卡规则
              </div>
              <el-form label-width="120px" size="small">
                <el-form-item label="打卡方式">
                  <el-radio-group v-model="checkinRule.type">
                    <el-radio label="WIFI">WiFi+人脸识别</el-radio>
                    <el-radio label="GPS">GPS定位</el-radio>
                    <el-radio label="NONE">不强制打卡</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item label="指定WiFi" v-if="checkinRule.type === 'WIFI'">
                  <el-select v-model="checkinRule.wifiId" placeholder="请选择WiFi热点" style="width: 300px;">
                    <el-option label="基地主WiFi" value="wifi-001"></el-option>
                    <el-option label="基地副WiFi" value="wifi-002"></el-option>
                    <el-option label="办公区WiFi" value="wifi-003"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="上班时间">
                  <el-time-picker v-model="checkinRule.workStartTime" format="HH:mm" style="width: 150px;"></el-time-picker>
                  <span style="margin: 0 10px;">下班时间:</span>
                  <el-time-picker v-model="checkinRule.workEndTime" format="HH:mm" style="width: 150px;"></el-time-picker>
                </el-form-item>
                <el-form-item label="迟到容差">
                  <el-input-number v-model="checkinRule.lateTolerance" :min="0" :max="60" style="width: 100px;"></el-input-number>
                  <span style="margin-left: 10px;">分钟</span>
                </el-form-item>
              </el-form>
            </div>
          </el-card>

          <el-card class="permission-card" v-else>
            <div class="no-selection">
              <i class="el-icon-warning-outline"></i>
              <p>请从左侧选择一个角色进行权限配置</p>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { getRoleList, getRolePermissions, getWorkbenchModules, saveRolePermissions } from '@/api/rolePermissionApi'
import { initDefaultPermissions as initPermissions } from '@/api/rolePermissionApi'

export default {
  name: 'PermissionConfig',
  data() {
    return {
      selectedRole: null,
      roles: [],
      appPermissions: [],
      workbenchPermissions: [],
      dataScope: 'ROLE',
      operationPermissions: [],
      initLoading: false,
      checkinRule: {
        type: 'WIFI',
        wifiId: 'wifi-001',
        workStartTime: new Date(2026, 0, 1, 8, 30),
        workEndTime: new Date(2026, 0, 1, 18, 0),
        lateTolerance: 5
      },
      workbenchModules: [
        { code: 'employee-manage', name: '员工管理', description: '员工列表入口', group: 'system' },
        { code: 'role-manage', name: '角色管理', description: '角色列表入口', group: 'system' },
        { code: 'permission-config', name: '权限配置', description: '权限配置入口', group: 'system' },
        { code: 'checkin-manage', name: '考勤管理', description: '考勤统计入口', group: 'checkin' },
        { code: 'leave-approve', name: '请假审批', description: '审批请假申请', group: 'checkin' },
        { code: 'makeup-approve', name: '补卡审批', description: '审批补卡申请', group: 'checkin' },
        { code: 'student-manage', name: '学员管理', description: '学员列表入口', group: 'student' },
        { code: 'customer-manage', name: '客户管理', description: '客户列表入口', group: 'customer' },
        { code: 'payment-audit', name: '缴费审核', description: '审核缴费凭证', group: 'payment' },
        { code: 'driver-manage', name: '司机任务', description: '任务列表入口', group: 'driver' },
        { code: 'coach-manage', name: '教练功能', description: '培训签到入口', group: 'coach' },
        { code: 'finance-manage', name: '财务管理', description: '财务报表入口', group: 'finance' },
        { code: 'salary-manage', name: '薪资核算', description: '工资台账入口', group: 'salary' },
        { code: 'sales-report', name: '业绩报表', description: '业绩统计入口', group: 'sales' }
      ]
    }
  },
  computed: {
    currentRoleName() {
      const role = this.roles.find(r => r.roleCode === this.selectedRole)
      return role ? role.roleName : ''
    }
  },
  mounted() {
    this.loadRoles()
    this.loadWorkbenchModules()
    // 如果有 URL 参数，自动选中对应角色
    if (this.$route.query.role) {
      this.$nextTick(() => {
        this.selectRole(this.$route.query.role)
      })
    }
  },
  methods: {
    async loadRoles() {
      try {
        const res = await getRoleList()
        this.roles = res.map(r => ({
          ...r,
          icon: r.icon || 'el-icon-s-custom',
          color: r.color || '#909399'
        }))
      } catch (e) {
        console.error('加载角色列表失败', e)
      }
    },
    async loadWorkbenchModules() {
      try {
        const res = await getWorkbenchModules()
        this.workbenchModules = res || []
      } catch (e) {
        console.error('加载工作台模块失败', e)
      }
    },
    async initDefaultPermissions() {
      try {
        this.initLoading = true
        await initPermissions()
        this.$message.success('默认权限初始化成功')
        // 重新加载角色列表
        await this.loadRoles()
      } catch (e) {
        this.$message.error(e.message || '初始化失败')
      } finally {
        this.initLoading = false
      }
    },
    goBack() {
      this.$router.push('/home/workbench')
    },
    async selectRole(roleCode) {
      this.selectedRole = roleCode
      await this.loadRolePermission(roleCode)
    },
    async loadRolePermission(roleCode) {
      try {
        const res = await getRolePermissions(roleCode)
        this.appPermissions = res.appPermissions || []
        this.workbenchPermissions = res.workbenchPermissions || []
        this.dataScope = res.dataScope || 'SELF'
        this.operationPermissions = res.operationPermissions || []
        if (res.checkinRule) {
          this.checkinRule = {
            type: res.checkinRule.type || 'WIFI',
            wifiId: res.checkinRule.wifiId || 'wifi-001',
            workStartTime: new Date(2026, 0, 1, res.checkinRule.workStartTime ? parseInt(res.checkinRule.workStartTime.split(':')[0]) : 8, res.checkinRule.workStartTime ? parseInt(res.checkinRule.workStartTime.split(':')[1]) : 30),
            workEndTime: new Date(2026, 0, 1, res.checkinRule.workEndTime ? parseInt(res.checkinRule.workEndTime.split(':')[0]) : 18, res.checkinRule.workEndTime ? parseInt(res.checkinRule.workEndTime.split(':')[1]) : 0),
            lateTolerance: res.checkinRule.lateTolerance || 5
          }
        }
      } catch (e) {
        console.error('加载角色权限失败', e)
      }
    },
    async savePermission() {
      try {
        const permissions = {
          appPermissions: this.appPermissions,
          workbenchPermissions: this.workbenchPermissions,
          dataScope: this.dataScope,
          operationPermissions: this.operationPermissions,
          checkinRule: {
            type: this.checkinRule.type,
            wifiId: this.checkinRule.wifiId,
            workStartTime: this.checkinRule.workStartTime ? this.formatTime(this.checkinRule.workStartTime) : '08:30',
            workEndTime: this.checkinRule.workEndTime ? this.formatTime(this.checkinRule.workEndTime) : '18:00',
            lateTolerance: this.checkinRule.lateTolerance
          }
        }
        await saveRolePermissions(this.selectedRole, permissions)
        this.$message.success('权限配置已保存')
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      }
    },
    formatTime(date) {
      const h = date.getHours().toString().padStart(2, '0')
      const m = date.getMinutes().toString().padStart(2, '0')
      return `${h}:${m}`
    }
  }
}
</script>

<style scoped>
.permission-config-container {
  height: 100%;
  background: #f5f7fa;
  overflow: auto;
  padding-bottom: 20px;
}

.page-header {
  background: #fff;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left i {
  font-size: 18px;
  cursor: pointer;
  color: #606266;
}

.header-left i:hover {
  color: #409eff;
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.config-content {
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;
  margin: 15px 20px;
}

.role-list-card {
  border-radius: 4px;
  height: calc(100vh - 180px);
  overflow-y: auto;
  box-shadow: none !important;
}

.role-list {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

.role-item {
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #ebeef5;
}

.role-item:hover {
  background: #f5f7fa;
  border-color: #409eff;
}

.role-item.active {
  background: #ecf5ff;
  border-color: #409eff;
}

.role-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.role-icon i {
  font-size: 20px;
  color: #fff;
}

.role-info {
  flex: 1;
}

.role-name {
  font-weight: bold;
  color: #303133;
}

.role-perm-count {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.permission-card {
  min-height: calc(100vh - 180px);
}

.perm-group {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.perm-group:last-child {
  border-bottom: none;
}

.perm-group-title {
  font-weight: bold;
  margin-bottom: 16px;
  color: #303133;
  display: flex;
  align-items: center;
}

.perm-group-title i {
  margin-right: 8px;
  color: #409eff;
}

.perm-hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
  margin-left: 8px;
}

.perm-label {
  font-weight: bold;
  margin-right: 8px;
}

.perm-desc {
  font-size: 12px;
  color: #909399;
}

.el-checkbox, .el-radio {
  display: block;
  margin: 10px 0;
  margin-left: 0;
}

.no-selection {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.no-selection i {
  font-size: 48px;
  margin-bottom: 16px;
}

.no-selection p {
  font-size: 14px;
}
</style>
